/* ============================================
   COLLECTION PAGE - PRODUCTS FUNCTIONALITY
   ============================================ */

/**
 * Collection Products Class
 * Handles quick-add and color-swatch image switching for the manual block grid.
 */

class CollectionProducts {
  constructor() {
    this.productsGrid = document.querySelector('.collection-products__grid');
    this.productsContainer = document.querySelector('.collection-products');
    this.productCards = document.querySelectorAll('.collection-product-card:not(.collection-product-card--banner)');

    this.isLoading = false;

    if (this.productsGrid) {
      this.init();
    }
  }

  init() {
    this.attachEventListeners();
  }

  /**
   * Attach event listeners
   */
  attachEventListeners() {
    // Quick add buttons
    document.querySelectorAll('.collection-product-card__quick-add').forEach(btn => {
      btn.addEventListener('click', (e) => this.handleQuickAdd(e));
    });

    // Product card clicks
    document.querySelectorAll('.collection-product-card').forEach(card => {
      if (!card.classList.contains('collection-product-card--banner')) {
        card.addEventListener('click', (e) => this.navigateToProduct(e));
      }
    });

    // Color swatch clicks - image switching
    document.querySelectorAll('.collection-product-card__color').forEach(swatch => {
      swatch.addEventListener('click', (e) => this.handleColorSwatchClick(e));
    });
  }

  /**
   * Handle color swatch click - update product image and price
   * @param {Event} event
   */
  handleColorSwatchClick(event) {
    if (!(event instanceof Event)) return;
    event.stopPropagation();
    event.preventDefault();

    const swatch = event.currentTarget;
    if (!(swatch instanceof HTMLElement)) return;

    const card = swatch.closest('.collection-product-card');
    if (!card) return;

    const variantImage = swatch.getAttribute('data-variant-image');
    const variantHoverImage = swatch.getAttribute('data-variant-hover-image');
    const variantId = swatch.getAttribute('data-variant-id');
    const variantPrice = swatch.getAttribute('data-variant-price');
    const variantComparePrice = swatch.getAttribute('data-variant-compare-price');

    // Update active state on swatches
    const allSwatches = card.querySelectorAll('.collection-product-card__color');
    allSwatches.forEach(s => s.classList.remove('is-active'));
    swatch.classList.add('is-active');

    // Update product image if variant has an image
    if (variantImage) {
      const primaryImage = /** @type {HTMLImageElement|null} */ (card.querySelector('.collection-product-card__image--primary'));
      if (primaryImage) {
        primaryImage.src = variantImage;
        primaryImage.srcset = `${variantImage} 1000w`;
      }
    }

    // Update hover image if variant has one
    const hoverImage = /** @type {HTMLImageElement|null} */ (card.querySelector('.collection-product-card__image--hover'));
    if (variantHoverImage) {
      if (hoverImage) {
        hoverImage.src = variantHoverImage;
        hoverImage.srcset = `${variantHoverImage} 1000w`;
      } else {
        // Create hover image if it doesn't exist yet
        const wrapper = card.querySelector('.collection-product-card__image-wrapper');
        const primaryImg = card.querySelector('.collection-product-card__image--primary');
        if (wrapper && primaryImg) {
          const newHoverImg = document.createElement('img');
          newHoverImg.src = variantHoverImage;
          newHoverImg.srcset = `${variantHoverImage} 1000w`;
          newHoverImg.alt = (primaryImg.alt || '') + ' - alternate view';
          newHoverImg.className = primaryImg.className.replace('--primary', '--hover');
          newHoverImg.loading = 'lazy';
          newHoverImg.width = 600;
          newHoverImg.height = 600;
          primaryImg.after(newHoverImg);
          card.classList.add('has-hover-image');
        }
      }
    } else if (hoverImage) {
      // No hover image for this variant — remove it
      hoverImage.remove();
      card.classList.remove('has-hover-image');
    }

    // Update price display
    if (variantPrice) {
      const priceEl = card.querySelector('.collection-product-card__price');
      const comparePriceEl = /** @type {HTMLElement|null} */ (card.querySelector('.collection-product-card__price-original'));

      if (priceEl) {
        priceEl.textContent = this.formatMoney(parseInt(variantPrice));
      }

      if (comparePriceEl) {
        if (variantComparePrice && parseInt(variantComparePrice) > parseInt(variantPrice)) {
          comparePriceEl.textContent = this.formatMoney(parseInt(variantComparePrice));
          comparePriceEl.style.display = '';
        } else {
          comparePriceEl.style.display = 'none';
        }
      }
    }

    // Update quick add button variant ID
    const quickAddBtn = card.querySelector('.collection-product-card__quick-add');
    if (quickAddBtn && variantId) {
      quickAddBtn.setAttribute('data-variant-id', variantId);
    }

    // Update card URL to point to the selected variant
    if (variantId) {
      const baseUrl = (card.dataset.productUrl || '').split('?')[0];
      card.dataset.productUrl = baseUrl + '?variant=' + variantId;
    }

    // Update the variant name line (Variant Card)
    const variantName = swatch.getAttribute('data-variant-name');
    const nameDisplay = card.querySelector('[data-variant-name-display]');
    if (nameDisplay && variantName) {
      nameDisplay.textContent = variantName;
    }
  }

  /**
   * Format price in money format
   * @param {number} cents - Price in cents
   * @returns {string} Formatted price
   */
  formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2).replace(/\.00$/, '');
  }

  /**
   * Handle quick add
   */
  handleQuickAdd(event) {
    if (!(event instanceof Event)) return;

    event.stopPropagation();
    event.preventDefault();

    const btn = event.currentTarget;
    if (!(btn instanceof HTMLElement)) return;

    const card = btn.closest('.collection-product-card');
    const productTitle = card?.querySelector('.collection-product-card__title')?.textContent || 'Product';
    const showVariants = btn.getAttribute('data-show-variants') === 'true';
    const productHandle = btn.getAttribute('data-product-handle');

    // If accessory with variants, show variant popup
    if (showVariants && productHandle) {
      this.showVariantPopup(btn, productHandle, card);
      return;
    }

    // Standard quick add
    this.addToCartWithFeedback(btn, productTitle);
  }

  /**
   * Show variant popup for accessories/parts
   */
  showVariantPopup(btn, productHandle, card) {
    // Check if popup already exists
    let existingPopup = card.querySelector('.variant-popup');
    if (existingPopup) {
      existingPopup.classList.toggle('is-visible');
      return;
    }

    // Fetch product data
    fetch(`/products/${productHandle}.js`)
      .then(response => response.json())
      .then(product => {
        const popup = this.createVariantPopup(product, btn);
        card.querySelector('.collection-product-card__image-wrapper').appendChild(popup);

        // Show popup with animation
        requestAnimationFrame(() => {
          popup.classList.add('is-visible');
        });
      })
      .catch(error => {
        console.error('Error fetching product:', error);
        // Fallback: just add first variant
        const variantId = btn.getAttribute('data-variant-id');
        this.addToCart(variantId, btn);
      });
  }

  /**
   * Create variant popup HTML
   */
  createVariantPopup(product, btn) {
    const popup = document.createElement('div');
    popup.className = 'variant-popup';
    popup.setAttribute('role', 'dialog');
    popup.setAttribute('aria-label', 'Select variant');

    let variantHTML = product.variants.map((variant, index) => `
      <button
        class="variant-popup__option${index === 0 ? ' is-active' : ''}"
        data-variant-id="${variant.id}"
        data-variant-title="${variant.title}"
        data-variant-available="${variant.available}"
        ${!variant.available ? 'disabled' : ''}
        type="button"
      >
        <span class="variant-popup__option-title">${variant.title}</span>
        ${!variant.available ? '<span class="variant-popup__option-unavailable">Sold out</span>' : ''}
      </button>
    `).join('');

    popup.innerHTML = `
      <div class="variant-popup__header">
        <span class="variant-popup__label">Select Option</span>
        <button class="variant-popup__close" type="button" aria-label="Close">×</button>
      </div>
      <div class="variant-popup__options">
        ${variantHTML}
      </div>
    `;

    // Attach event listeners
    popup.querySelector('.variant-popup__close').addEventListener('click', (e) => {
      e.stopPropagation();
      popup.classList.remove('is-visible');
    });

    popup.querySelectorAll('.variant-popup__option').forEach(option => {
      option.addEventListener('click', (e) => {
        e.stopPropagation();
        const variantId = option.getAttribute('data-variant-id');
        const variantTitle = option.getAttribute('data-variant-title');

        // Update active state
        popup.querySelectorAll('.variant-popup__option').forEach(o => o.classList.remove('is-active'));
        option.classList.add('is-active');

        // Add to cart
        this.addToCart(variantId, btn, variantTitle);

        // Hide popup after selection
        setTimeout(() => {
          popup.classList.remove('is-visible');
        }, 300);
      });
    });

    // Close on click outside
    document.addEventListener('click', (e) => {
      if (!popup.contains(e.target) && !btn.contains(e.target)) {
        popup.classList.remove('is-visible');
      }
    }, { once: true });

    return popup;
  }

  /**
   * Add to cart with feedback animation
   */
  addToCartWithFeedback(btn, productTitle) {
    // Show feedback
    btn.style.background = '#000';
    btn.style.color = '#fff';
    btn.innerHTML = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"></polyline></svg>';

    // Reset after animation
    setTimeout(() => {
      btn.style.background = '';
      btn.style.color = '';
      btn.innerHTML = '<span class="plus-icon"></span>';
    }, 800);

    // In production: send to cart API
    const variantId = btn.getAttribute('data-variant-id');
    this.addToCart(variantId, btn, productTitle);
  }

  /**
   * Add item to cart via Shopify AJAX API
   */
  addToCart(variantId, btn, title = 'Product') {
    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        id: variantId,
        quantity: 1
      })
    })
    .then(response => response.json())
    .then(item => {
      console.log(`Added "${title}" to cart`, item);

      // Show success feedback
      btn.style.background = '#000';
      btn.style.color = '#fff';
      btn.innerHTML = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"></polyline></svg>';

      // Reset after animation
      setTimeout(() => {
        btn.style.background = '';
        btn.style.color = '';
        btn.innerHTML = '<span class="plus-icon"></span>';
      }, 800);

      // Dispatch event for cart drawer/counter updates
      document.dispatchEvent(new CustomEvent('cart:item-added', {
        detail: { item }
      }));
    })
    .catch(error => {
      console.error('Error adding to cart:', error);
      btn.innerHTML = '<span class="plus-icon"></span>';
    });
  }

  /**
   * Navigate to product page
   */
  navigateToProduct(event) {
    if (!(event instanceof Event)) return;

    const target = event.target;
    // Don't navigate if quick add button, color swatch, or link was clicked
    if (target instanceof HTMLElement) {
      if (target.closest('.collection-product-card__quick-add') ||
          target.closest('.collection-product-card__color') ||
          target.closest('a')) {
        return;
      }
    }

    const currentTarget = event.currentTarget;
    if (!(currentTarget instanceof HTMLElement)) return;

    // Get the product URL from data attribute
    const productUrl = currentTarget.dataset.productUrl;
    if (productUrl) {
      window.location.href = productUrl;
    }
  }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new CollectionProducts());
} else {
  new CollectionProducts();
}
